// ITER151 (Feb 2026) — Vista unificada CxP + Gastos Operativos para Gerencia
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Search, Eye } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import DetalleCuentaDialog from "@/components/DetalleCuentaDialog";

function defaultDesde() { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); }
function defaultHasta() { return new Date().toISOString().slice(0, 10); }

export default function PagosUnificado() {
  const [desde, setDesde] = useState(defaultDesde());
  const [hasta, setHasta] = useState(defaultHasta());
  const [filtroFecha, setFiltroFecha] = useState("emision"); // ITER206: emision | vencimiento
  const [estado, setEstado] = useState("all");
  const [formaPago, setFormaPago] = useState("");
  const [q, setQ] = useState("");
  const [proveedorFilter, setProveedorFilter] = useState("");
  const [incluirFacturas, setIncluirFacturas] = useState(true);
  const [incluirGastos, setIncluirGastos] = useState(true);
  const [data, setData] = useState({ items: [], totales: null, total: 0 });
  const [loading, setLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (desde) p.set("desde", desde);
      if (hasta) p.set("hasta", hasta);
      if (desde || hasta) p.set("filtro_fecha", filtroFecha);
      if (estado && estado !== "all") p.set("estado", estado);
      if (formaPago) p.set("forma_pago", formaPago);
      if (q) p.set("q", q);
      p.set("incluir_facturas", String(incluirFacturas));
      p.set("incluir_gastos", String(incluirGastos));
      p.set("limit", "1000");
      const { data: d } = await api.get(`/accounting/cuentas-pagar-unificado?${p.toString()}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const f = (proveedorFilter || "").toLowerCase().trim();
    if (!f) return data.items;
    return (data.items || []).filter(x => (x.proveedor_nombre || "").toLowerCase().includes(f));
  }, [data.items, proveedorFilter]);

  const cols = [
    { label: "Tipo", accessor: "tipo" },
    { label: "Documento", accessor: "numero_documento" },
    { label: "Fecha", accessor: "fecha" },
    { label: "Vencimiento", accessor: "fecha_vencimiento" },
    { label: "Proveedor", accessor: "proveedor_nombre" },
    { label: "Concepto", accessor: "concepto" },
    { label: "Forma pago", accessor: "forma_pago" },
    { label: "Monto", accessor: (r) => `$${(r.monto_total || 0).toFixed(2)}` },
    { label: "Pagado", accessor: (r) => `$${(r.monto_pagado || 0).toFixed(2)}` },
    { label: "Retención", accessor: (r) => `$${(r.retencion_aplicada || 0).toFixed(2)}` },
    { label: "Saldo", accessor: (r) => `$${(r.saldo || 0).toFixed(2)}` },
    { label: "Estado", accessor: "estado" },
  ];

  const verDetalle = (it) => {
    if (it.tipo === "cxp") {
      setDetalle({ id: it.id, kind: "cxp" });
    } else {
      toast.message("Detalle de gasto operativo: abre el módulo Gastos para ver/editar.");
    }
  };

  const cargarDetalle = async (id) => {
    try {
      const { data: d } = await api.get(`/accounting/cuentas-pagar/${id}/detalle`);
      return d;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };
  const [detalleData, setDetalleData] = useState(null);
  useEffect(() => {
    if (detalle?.id && detalle.kind === "cxp") {
      cargarDetalle(detalle.id).then(setDetalleData);
    } else {
      setDetalleData(null);
    }
  }, [detalle]);

  const estadoBadge = (e) => {
    if (e === "pagada") return <Badge className="bg-[#205c45] text-white">Pagada</Badge>;
    if (e === "parcial") return <Badge className="bg-[#b08400] text-white">Parcial</Badge>;
    if (e === "vencida") return <Badge variant="destructive">Vencida</Badge>;
    if (e === "anulada_nc") return <Badge variant="outline">NC total</Badge>;
    return <Badge variant="outline">{e || "—"}</Badge>;
  };

  return (
    <div className="space-y-5" data-testid="pagos-unificado-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Gerencia · Contabilidad</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <Building2 className="inline mr-2 mb-1" size={28} />
            Pagos (Facturas + Gastos)
          </h1>
          <p className="text-sm text-[#4a5c53] mt-1">Vista unificada de CxP de proveedores y gastos operativos. Filtra por proveedor, forma de pago, estado y fechas.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-end p-3 rounded-md border border-[#d1dbd6] bg-white">
        <div>
          <Label className="text-[10px] uppercase">Rango por</Label>
          <Select value={filtroFecha} onValueChange={setFiltroFecha}>
            <SelectTrigger className="h-8 w-44" data-testid="pagos-filtro-fecha"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="emision">Fecha de emisión</SelectItem>
              <SelectItem value="vencimiento">Fecha de vencimiento</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] uppercase">Desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-8" /></div>
        <div><Label className="text-[10px] uppercase">Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-8" /></div>
        <div>
          <Label className="text-[10px] uppercase">Estado</Label>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="parcial">Parcial</SelectItem>
              <SelectItem value="pagada">Pagada</SelectItem>
              <SelectItem value="vencida">Vencidas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase">Forma pago</Label>
          <Input value={formaPago} onChange={(e) => setFormaPago(e.target.value)} placeholder="efectivo, transferencia…" className="h-8 w-40" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-[10px] uppercase">Buscar (servidor)</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proveedor, # doc, descripción…" className="h-8" />
        </div>
        <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={incluirFacturas} onChange={(e) => setIncluirFacturas(e.target.checked)} />Facturas</label>
        <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={incluirGastos} onChange={(e) => setIncluirGastos(e.target.checked)} />Gastos</label>
        <Button onClick={load} disabled={loading} className="bg-[#205c45] hover:bg-[#1a4a37] h-8" data-testid="aplicar-filtros"><Search size={14} className="mr-1" />Aplicar</Button>
        <div>
          <Label className="text-[10px] uppercase">Filtro local (proveedor)</Label>
          <Input value={proveedorFilter} onChange={(e) => setProveedorFilter(e.target.value)} placeholder="Filtra resultados…" className="h-8 w-44" />
        </div>
        <ExportButton title={`Pagos ${desde} → ${hasta}`} columns={cols} rows={filtered} filename={`pagos-${desde}-${hasta}`} />
      </div>

      {data.totales && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Items" value={`${data.total}`} />
          <KPI label="CxP" value={data.totales.n_cxp} />
          <KPI label="Gastos" value={data.totales.n_gastos} />
          <KPI label="Monto total" value={`$${data.totales.monto_total.toFixed(2)}`} />
          <KPI label="Saldo pendiente" value={`$${data.totales.saldo.toFixed(2)}`} highlight />
        </div>
      )}

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-x-auto">
        <table className="erp-table">
          <thead><tr>
            <th>Tipo</th><th>Documento</th><th>Fecha</th><th>Vence</th><th>Proveedor</th>
            <th>Concepto</th><th>F. pago</th>
            <th className="text-right">Monto</th><th className="text-right">Pagado</th>
            <th className="text-right">Retención</th><th className="text-right">Saldo</th>
            <th>Estado</th><th></th>
          </tr></thead>
          <tbody>
            {filtered.map(it => (
              <tr key={`${it.tipo}-${it.id}`} data-testid={`pago-row-${it.id}`}>
                <td>
                  {it.tipo === "cxp"
                    ? <Badge className="bg-[#2d4a73] text-white text-[10px]">CxP</Badge>
                    : <Badge className="bg-[#9b6a00] text-white text-[10px]">Gasto</Badge>}
                </td>
                <td className="font-mono text-xs">{it.numero_documento}</td>
                <td className="text-xs">{it.fecha}</td>
                <td className="text-xs">{it.fecha_vencimiento || "—"}</td>
                <td className="font-medium">{it.proveedor_nombre}</td>
                <td className="text-xs">{it.concepto}</td>
                <td className="text-xs">{it.forma_pago || "—"}</td>
                <td className="text-right tabular-nums">${(it.monto_total || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums">${(it.monto_pagado || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums">${(it.retencion_aplicada || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">${(it.saldo || 0).toFixed(2)}</td>
                <td>{estadoBadge(it.estado)}</td>
                <td>
                  <Button size="icon" variant="ghost" onClick={() => verDetalle(it)} title="Ver detalle" data-testid={`ver-${it.id}`}>
                    <Eye size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={13} className="text-center py-10 text-[#8ba397]">Sin movimientos</td></tr>}
          </tbody>
        </table>
      </div>

      <DetalleCuentaDialog
        data={detalleData}
        kind="cxp"
        open={!!detalle && detalle.kind === "cxp"}
        onClose={() => { setDetalle(null); setDetalleData(null); }}
        onRefresh={load}
      />
    </div>
  );
}

function KPI({ label, value, highlight }) {
  return (
    <div className={`rounded-md border ${highlight ? "border-[#205c45] bg-[#f3f8f5]" : "border-[#d1dbd6] bg-white"} p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">{label}</div>
      <div className={`text-lg font-semibold ${highlight ? "text-[#205c45]" : "text-[#15241d]"} tabular-nums mt-1`}>{value}</div>
    </div>
  );
}
